import { Link } from "react-router-dom";
import { STUDIO_URL } from "../lib/runtimeConfig.js";
import Seo from "../components/Seo.jsx";

const STEPS = [
  { num: "01", title: "Tell Us Your Plans", body: "Share your destination, dates, budget, and travel style." },
  { num: "02", title: "We Curate Your Options", body: "Flights, stays, and experiences tailored to you — no guesswork." },
  { num: "03", title: "Confirm & Pay Securely", body: "Clear pricing, a transparent process, and secure payment." },
  { num: "04", title: "Travel With Support", body: "We stay available before and during your trip." },
];

const SERVICES = [
  { ico: "✈", title: "Flights", to: "/flights", body: "Economy to first class, point-to-point or multi-city — sourced and booked for you, with a real person to call." },
  { ico: "◈", title: "Stays", to: "/stays", body: "Hotels, safari lodges, villas and beach resorts, hand-picked and vetted so you arrive somewhere you'll love." },
  { ico: "❖", title: "Visas", to: "/visas", body: "Requirements, documents and timelines explained clearly — and checked before you submit." },
  { ico: "✦", title: "Tours & Experiences", to: "/experiences", body: "Safaris, private guides and the moments you'll still be talking about years later." },
  { ico: "⇄", title: "Transfers", to: "/experiences", body: "Airport pickups and ground transport that simply show up — no haggling at arrivals." },
  { ico: "✪", title: "Custom Trips", to: "/plan-a-trip", body: "Multi-stop journeys across Africa and beyond, shaped around your dates, pace and budget." },
];

// Real hero photo for the cinematic band. The file lives in /public, so it's
// referenced by absolute path (Vite serves /public at the site root). Set to ""
// to fall back to the built-in illustrated savanna scene.
const CINEMATIC_IMAGE = "/hero-savanna.webp";

// Destination tiles use real photography from /public/destinations.
const DESTINATIONS = [
  { name: "Nigeria", tag: "Lagos • Calabar", img: "/destinations/destination-nigeria.webp" },
  { name: "Ghana", tag: "Accra • Cape Coast", img: "/destinations/destination-ghana.webp" },
  { name: "Kenya", tag: "Nairobi • Maasai Mara", img: "/destinations/destination-kenya.webp" },
  { name: "Rwanda", tag: "Kigali • Volcanoes NP", img: "/destinations/destination-rwanda.webp" },
  { name: "South Africa", tag: "Cape Town • Kruger", img: "/destinations/destination-south-africa.webp" },
  { name: "Morocco", tag: "Marrakech • Fès", img: "/destinations/destination-morocco.webp" },
  { name: "Tanzania", tag: "Serengeti • Zanzibar", img: "/destinations/destination-tanzania.webp" },
  { name: "Namibia", tag: "Sossusvlei • Swakopmund", img: "/destinations/destination-namibia.webp" },
];

export default function Home() {
  return (
    <div className="page">
      <Seo
        title="513Travel — Africa-first travel planning"
        description="Plan flights, stays, visas, tours and custom journeys across Africa and beyond with calm human support, secure payments and multilingual service."
        path="/"
      />
      {/* ===== 1+2. Editorial hero ===== */}
      <section className="hero">
        <div className="container">
          <div className="eyebrow">513Travel — Explore Africa, go global</div>
          <h1>Africa-first travel, planned beautifully.</h1>
          <p className="subtitle">
            Plan flights, stays, visas, tours and custom trips with intelligent
            support, secure payments and multilingual service across Africa and
            beyond.
          </p>
          <div className="hero-cta-row">
            <Link to="/plan-a-trip" className="btn gold">Plan a Trip</Link>
            <Link to="/track-trip" className="btn outline">Track My Trip</Link>
          </div>
          <p className="trust-line">
            <b>Fast response</b> • <b>Secure payments</b> • <b>Multilingual support</b>
          </p>
        </div>
      </section>

      {/* ===== 3. Cinematic band ===== */}
      <section className="cinematic" aria-hidden={CINEMATIC_IMAGE ? undefined : "true"}>
        {CINEMATIC_IMAGE ? (
          <img
            className="cinematic-img"
            src={CINEMATIC_IMAGE}
            alt="Golden sunrise over an African savanna landscape"
            width="1672"
            height="941"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <SavannaScene />
        )}
      </section>

      {/* Emotional editorial line beneath the band */}
      <section className="emotional">
        <div className="container">
          <p className="emotional-line">
            From sunrise on the savanna to the cities that never sleep.
          </p>
        </div>
      </section>

      {/* ===== 4. Four-step process ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>Planned in four calm steps</h2>
            <p>A guided process from first idea to safe return — with a human on your side throughout.</p>
          </div>
          <div className="grid grid-4">
            {STEPS.map((s) => (
              <div className="step-card" key={s.num}>
                <span className="step-num" aria-hidden="true">{s.num}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. Services ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What we handle</div>
            <h2>Everything for the journey</h2>
            <p>One team for the whole trip — so the pieces fit together instead of fighting each other.</p>
          </div>
          <div className="grid grid-3">
            {SERVICES.map((s) => (
              <Link to={s.to} className="service-card" key={s.title}>
                <span className="service-ico" aria-hidden="true">{s.ico}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <span className="service-link">Explore →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6. Africa-first (green) ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Africa-first</div>
            <h2>Built for African journeys, ready for the world.</h2>
            <p>
              We know the routes, the seasons, and the realities of travelling
              across the continent — and we bring the same care to trips
              anywhere beyond it.
            </p>
            <ul className="feature-points">
              <li>Deep coverage of African routes and destinations</li>
              <li>Multilingual service — English, French, Portuguese, Arabic, Swahili</li>
              <li>Secure payments in local and international currencies</li>
              <li>Real human support before, during, and after travel</li>
            </ul>
            <div className="page-actions">
              <Link to="/africa" className="btn light">Explore Africa</Link>
              <Link to="/plan-a-trip" className="btn outline-light">Plan a Trip</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 7. Destination preview ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Where to next</div>
            <h2>Destinations we love</h2>
            <p>A starting point, not a limit — tell us where you dream of and we'll make it happen.</p>
          </div>
          <div className="grid grid-4">
            {DESTINATIONS.map((d) => (
              <Link to="/plan-a-trip" className="dest-card" key={d.name}>
                <div className="dest-image">
                  <img src={d.img} alt={`${d.name} — ${d.tag}`} loading="lazy" decoding="async" />
                </div>
                <div className="dest-body">
                  <h3>{d.name}</h3>
                  <p>{d.tag}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 8. Studio CTA ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="studio-cta">
            <div>
              <div className="eyebrow">For travel businesses, creators and agencies</div>
              <h2>Build, package and promote your travel offers.</h2>
              <p>
                513Travel Studio gives travel businesses the tools to create and
                market beautiful travel content and offers.
              </p>
            </div>
            <div>
              <a href={STUDIO_URL} target="_blank" rel="noopener noreferrer" className="btn gold">
                Visit 513Travel Studio ↗
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Self-contained savanna horizon: layered dusk hills, a soft low sun, distant
   birds and acacia silhouettes. Kept subtle and natural so it reads as a real
   landscape, not an abstract graphic. Swappable for a photo via CINEMATIC_IMAGE. */
function Acacia({ x, y, s = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M-3 0 L-5 -64 Q-2 -72 0 -72 Q2 -72 5 -64 L3 0 Z" />
      <path d="M-78 -70 Q-30 -86 0 -80 Q30 -86 78 -70 Q40 -80 16 -82 Q40 -90 60 -96 Q24 -92 0 -90 Q-24 -92 -60 -96 Q-40 -90 -16 -82 Q-40 -80 -78 -70 Z" />
    </g>
  );
}

function SavannaScene() {
  return (
    <svg viewBox="0 0 1200 460" preserveAspectRatio="xMidYMid slice" role="img" aria-label="An African savanna at sunrise">
      <defs>
        <radialGradient id="sun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fdeec2" stopOpacity="0.95" />
          <stop offset="60%" stopColor="#f3d089" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#f3d089" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* soft low sun, set into the haze */}
      <circle cx="780" cy="300" r="190" fill="url(#sun)" />
      <circle cx="780" cy="300" r="66" fill="#fbe6ac" opacity="0.92" />

      {/* distant birds */}
      <g stroke="#2a3a2c" strokeWidth="2.5" fill="none" opacity="0.55" strokeLinecap="round">
        <path d="M250 150 q12 -10 24 0 q12 -10 24 0" />
        <path d="M320 178 q9 -8 18 0 q9 -8 18 0" />
        <path d="M210 196 q9 -8 18 0 q9 -8 18 0" />
      </g>

      {/* far hill */}
      <path d="M0 360 Q300 322 620 350 T1200 342 V460 H0 Z" fill="#3a4d39" opacity="0.55" />
      {/* mid ground */}
      <path d="M0 392 Q360 360 720 388 T1200 384 V460 H0 Z" fill="#26331f" opacity="0.85" />
      {/* near ground + trees */}
      <g fill="#161f14">
        <path d="M0 418 Q300 398 600 414 T1200 410 V460 H0 Z" />
        <g opacity="0.92"><Acacia x={170} y={416} s={1.05} /></g>
        <g opacity="0.85"><Acacia x={1010} y={412} s={0.82} /></g>
        <g opacity="0.7"><Acacia x={560} y={420} s={0.5} /></g>
      </g>
    </svg>
  );
}
